import React, { Component, ReactNode } from 'react';
import { firestore } from '@/firebase/firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { FirebaseError } from "@firebase/util";
import Custom500 from "@/pages/500";
import * as Sentry from "@sentry/nextjs";

// Utility function to check if the code is running on the server
const isServer = typeof window === "undefined";
interface ErrorBoundaryProps {
  children: ReactNode;
  uid: string; // Pass user UID
  maxErrorCount?: number;
  customErrorComponent?: () => ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  errorCount: number;
  maxErrorCount: number;
  userAgent: string;
  os: string;
  deviceType: string;
  networkStatus: string;
  connectionType: string;
  viewportSize: string;
  screenResolution: string;
}
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    const {
      maxErrorCount
    } = this.props;

    // Initialize with safe default values for SSR
    this.state = {
      hasError: false,
      errorCount: 0,
      maxErrorCount: maxErrorCount || 1,
      userAgent: '',
      os: 'Unknown OS',
      deviceType: 'Desktop',
      networkStatus: 'unknown',
      connectionType: 'unknown',
      viewportSize: 'unknown',
      screenResolution: 'unknown'
    };
  }
  componentDidMount() {
    // Only run this code in the browser
    if (!isServer) {
      const userAgent = navigator.userAgent;
      const connection = (navigator as any).connection;
      const connectionType = connection ? connection.effectiveType : 'unknown';
      this.setState({
        userAgent,
        os: this.getOSInfo(),
        deviceType: this.getDeviceType(),
        connectionType,
        networkStatus: navigator.onLine ? 'online' : 'offline',
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      });
    }
  }
  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error | FirebaseError, errorInfo: React.ErrorInfo) {
    console.error('Error caught in ErrorBoundary:', error, errorInfo);

    // Capture the error and additional info using Sentry
    this.logErrorToSentry(error, errorInfo);

    //Log the error to Firestore
    this.logErrorToFirestore(error, errorInfo);

    // Increment error count and reset `hasError` to allow re-render
    this.setState(prevState => {
      const newErrorCount = prevState.errorCount + 1;
      return {
        errorCount: newErrorCount,
        hasError: newErrorCount >= this.state.maxErrorCount // Only reset if under the maxErrorCount
      };
    });
  }
  getOSInfo(): string {
    if (isServer) return 'Unknown OS'; // Return default if on the server

    const userAgent = navigator.userAgent;
    if (userAgent.includes("Windows")) return "Windows";
    if (userAgent.includes("Mac")) return "MacOS";
    if (userAgent.includes("Linux")) return "Linux";
    if (/Android/.test(userAgent)) return "Android";
    if (/iPhone|iPad|iPod/.test(userAgent)) return "iOS";
    return "Unknown OS";
  }
  getDeviceType(): string {
    if (isServer) return 'Desktop'; // Return default if on the server

    const userAgent = navigator.userAgent;
    if (/Mobi|Android/i.test(userAgent)) return "Mobile";
    if (/iPad|Tablet/i.test(userAgent)) return "Tablet";
    return "Desktop";
  }
  logErrorToSentry(error: Error, errorInfo: React.ErrorInfo) {
    const {
      uid
    } = this.props;
    const {
      networkStatus,
      connectionType,
      viewportSize,
      screenResolution
    } = this.state;
    const componentStack = errorInfo.componentStack;
    const componentName = this.extractComponentName(componentStack || '');
    const url = !isServer ? window.location.href : 'unknown';
    const referrer = !isServer ? document.referrer : 'unknown';
    Sentry.getCurrentScope().clear();
    Sentry.setContext("Urls", {
      "URL": url,
      "Referrer": referrer
    });
    Sentry.setContext("Screen", {
      "Screen Resolution": screenResolution,
      "Viewport Size": viewportSize
    });
    Sentry.setContext("Connection", {
      "Network Status": networkStatus,
      "Connection Type": connectionType
    });
    Sentry.setTag("component", componentName);
    Sentry.setUser({
      "uid": uid || "unknown"
    });
    Sentry.captureException(error);
  }
  logErrorToFirestore(error: Error, errorInfo: React.ErrorInfo) {
    const {
      uid
    } = this.props;
    const {
      userAgent,
      os,
      deviceType,
      networkStatus,
      connectionType,
      viewportSize,
      screenResolution
    } = this.state;
    const componentStack = errorInfo.componentStack;
    const componentName = this.extractComponentName(componentStack || '');
    const currentUrl = !isServer ? window.location.href : 'unknown';
    const referrer = !isServer ? document.referrer : 'unknown';
    const errorData = {
      uid,
      errorMessage: error.message,
      errorInfo: JSON.stringify(errorInfo),
      componentName,
      dateCreated: serverTimestamp(),
      currentUrl,
      referrer,
      userAgent,
      os,
      deviceType,
      networkStatus,
      connectionType,
      viewportSize,
      screenResolution
    };
    addDoc(collection(firestore, 'ErrorReporting'), errorData).then(() => {
      console.log('Error logged to Firestore');
    }).catch(err => {
      console.error('Error logging to Firestore:', err);
    });
  }
  extractComponentName(componentStack: string): string {
    const matches = componentStack.match(/at (\w+)/);
    return matches ? matches[1] : 'Unknown';
  }
  render() {
    const {
      hasError,
      errorCount
    } = this.state;

    // Display fallback UI if errorCount has reached maxErrorCount
    if (hasError && errorCount >= this.state.maxErrorCount) {
      if (this.props.customErrorComponent) {
        return this.props.customErrorComponent();
      }
      return <Custom500 data-sentry-element="Custom500" data-sentry-component="ErrorBoundary" data-sentry-source-file="ErrorBoundary.tsx" />;
    }
    return this.state.hasError ? null : this.props.children;
  }
}
export default ErrorBoundary;